import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

function EmailConfirmation() {
  const { t } = useLanguage();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [invitationData, setInvitationData] = useState(null);
  const [registrationForm, setRegistrationForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    display_name: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();

  useEffect(() => {
    const processToken = async () => {
      try {
        // Check if token is likely a community invitation token
        // Community invitation tokens are typically longer (base64 encoded with timestamp)
        const isCommunityInvitation = token.length > 24;
        
        if (isCommunityInvitation) {
          // For community invitations, check if we're already logged in
          if (isAuthenticated) {
            // Process the invitation for existing logged-in user
            try {
              const communityId = 1; // Default for now
              const response = await api.post('/communities/process-invitation', {
                community_id: communityId,
                token: token
              });
              
              setStatus('success');
              setMessage(response.data.message || t('auth.emailVerification.joinedCommunity'));

              // Redirect to dashboard after success
              setTimeout(() => navigate('/dashboard'), 3000);
            } catch (err) {
              setStatus('error');
              setMessage(err.response?.data?.message || t('auth.emailVerification.joinFailed'));
            }
          } else {
            // Not logged in, show registration form for invited user
            try {
              // In a real implementation, we would first validate the token
              // with the backend to get the email and community info
              // For now, we'll just show the registration form
              setStatus('registration');
              setInvitationData({
                token: token,
                communityId: 1,  // Default for now
                email: "Invited user's email would be extracted from token"
              });
            } catch (err) {
              setStatus('error');
              setMessage(t('auth.emailVerification.verificationFailed'));
            }
          }
        } else {
          // This is a regular email verification token
          const response = await api.get(`/verify-email/${token}`);
          setStatus('success');
          setMessage(response.data.message || t('auth.emailVerification.verificationSuccess'));
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.message || t('auth.emailVerification.verificationFailed'));
      }
    };

    processToken();
  }, [token, navigate, isAuthenticated, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRegistrationForm({
      ...registrationForm,
      [name]: value
    });
    
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  const validateForm = () => {
    const errors = {};

    if (!registrationForm.email.trim()) {
      errors.email = t('auth.emailVerification.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(registrationForm.email)) {
      errors.email = t('auth.emailVerification.emailInvalid');
    }

    if (!registrationForm.password) {
      errors.password = t('auth.emailVerification.passwordRequired');
    } else if (registrationForm.password.length < 6) {
      errors.password = t('auth.emailVerification.passwordMinLength');
    }

    if (registrationForm.password !== registrationForm.confirmPassword) {
      errors.confirmPassword = t('auth.emailVerification.passwordsDoNotMatch');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setStatus('submitting');
    
    try {
      const registrationData = {
        email: registrationForm.email,
        password: registrationForm.password,
        display_name: registrationForm.display_name || registrationForm.email.split('@')[0],
        community_id: invitationData.communityId || 1, // Default to community ID 1 if not specified
        invitation_token: token // Use the token from the URL params
      };
      
      const response = await api.post('/register-invited-user', registrationData);
      
      setStatus('success');
      setMessage(response.data.message || t('auth.emailVerification.registrationSuccess'));

      // Auto-login the user if the backend provides credentials
      if (response.data.auto_login) {
        await login(registrationForm.email, registrationForm.password);
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        // Otherwise redirect to login after a delay
        setTimeout(() => navigate('/login'), 3000);
      }

    } catch (error) {
      setStatus('registration');
      setMessage(error.response?.data?.message || t('auth.emailVerification.registrationFailed'));
    }
  };

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">
                {status === 'registration' ? t('auth.emailVerification.completeRegistration') : t('auth.emailVerification.title')}
              </h4>
            </div>
            <div className="card-body">
              {status === 'loading' && (
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </div>
                  <p className="mt-3">{t('auth.emailVerification.processingRequest')}</p>
                </div>
              )}

              {status === 'submitting' && (
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </div>
                  <p className="mt-3">{t('auth.emailVerification.creatingAccount')}</p>
                </div>
              )}

              {status === 'registration' && (
                <>
                  <div className="alert alert-info">
                    <p className="mb-0">{t('auth.emailVerification.invitedToJoin')}</p>
                  </div>

                  {message && <div className="alert alert-danger">{message}</div>}

                  <form onSubmit={handleRegistrationSubmit}>
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">{t('auth.emailVerification.email')}</label>
                      <input
                        type="email"
                        className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                        id="email"
                        name="email"
                        value={registrationForm.email}
                        onChange={handleInputChange}
                        required
                      />
                      {formErrors.email && (
                        <div className="invalid-feedback">{formErrors.email}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="password" className="form-label">{t('auth.emailVerification.password')}</label>
                      <input
                        type="password"
                        className={`form-control ${formErrors.password ? 'is-invalid' : ''}`}
                        id="password"
                        name="password"
                        value={registrationForm.password}
                        onChange={handleInputChange}
                        required
                      />
                      {formErrors.password && (
                        <div className="invalid-feedback">{formErrors.password}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="confirmPassword" className="form-label">{t('auth.emailVerification.confirmPassword')}</label>
                      <input
                        type="password"
                        className={`form-control ${formErrors.confirmPassword ? 'is-invalid' : ''}`}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={registrationForm.confirmPassword}
                        onChange={handleInputChange}
                        required
                      />
                      {formErrors.confirmPassword && (
                        <div className="invalid-feedback">{formErrors.confirmPassword}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="display_name" className="form-label">{t('auth.emailVerification.displayName')}</label>
                      <input
                        type="text"
                        className="form-control"
                        id="display_name"
                        name="display_name"
                        value={registrationForm.display_name}
                        onChange={handleInputChange}
                      />
                      <div className="form-text">
                        {t('auth.emailVerification.displayNameHelp')}
                      </div>
                    </div>

                    <button type="submit" className="btn btn-primary w-100">
                      {t('auth.emailVerification.completeRegistrationButton')}
                    </button>
                  </form>
                </>
              )}
              
              {status === 'redirect' && (
                <div className="text-center">
                  <div className="alert alert-info">
                    <p>{message}</p>
                  </div>
                  <div>
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                      <span className="visually-hidden">{t('auth.emailVerification.redirectingToLogin')}</span>
                    </div>
                    <span>{t('auth.emailVerification.redirectingToLogin')}</span>
                  </div>
                </div>
              )}

              {status === 'success' && (
                <div className="text-center">
                  <div className="alert alert-success">{message}</div>
                  {!isAuthenticated ? (
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate('/login')}
                    >
                      {t('auth.emailVerification.continueToLogin')}
                    </button>
                  ) : (
                    <div className="mt-3">
                      <div className="spinner-border spinner-border-sm text-success me-2" role="status">
                        <span className="visually-hidden">{t('auth.emailVerification.redirectingToDashboard')}</span>
                      </div>
                      <span>{t('auth.emailVerification.redirectingToDashboard')}</span>
                    </div>
                  )}
                </div>
              )}

              {status === 'error' && (
                <div className="text-center">
                  <div className="alert alert-danger">{message}</div>
                  <button
                    className="btn btn-secondary"
                    onClick={() => navigate('/')}
                  >
                    {t('auth.emailVerification.returnHome')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailConfirmation;